import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { ClerkProvider } from "@clerk/clerk-react";
import { Toaster } from "sonner";
import App from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";
import CookieBanner from "./components/CookieBanner";
import { queryClient } from "./lib/queryClient";
import { initSentry } from "./lib/sentry";
import "./index.css";

initSentry();

const clerkKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root not found");
const root = createRoot(rootEl);

if (!clerkKey) {
  // First-run helper: render a placeholder so `pnpm dev` doesn't crash with a
  // confusing Clerk error. Add VITE_CLERK_PUBLISHABLE_KEY to .env.local
  // (see artifacts/web/.env.example) to enable auth.
  root.render(
    <StrictMode>
      <ConfigurePlaceholder />
    </StrictMode>,
  );
} else {
  root.render(
    <StrictMode>
      <ClerkProvider
        publishableKey={clerkKey}
        afterSignOutUrl="/login"
        appearance={{
          variables: {
            colorPrimary: "#F97316",
            colorBackground: "#111318",
            colorText: "#FFFFFF",
            colorInputBackground: "#1F2933",
            colorInputText: "#FFFFFF",
            colorTextSecondary: "#9CA3AF",
          },
        }}
      >
        <QueryClientProvider client={queryClient}>
          <ErrorBoundary>
            <App />
          </ErrorBoundary>
          <CookieBanner />
          <Toaster
            position="top-right"
            theme="dark"
            toastOptions={{
              style: { background: "#1F2933", color: "#FFFFFF" },
            }}
          />
        </QueryClientProvider>
      </ClerkProvider>
    </StrictMode>,
  );
}

function ConfigurePlaceholder() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#0B0B0C",
        color: "#FFFFFF",
        fontFamily: "Inter, system-ui, sans-serif",
        padding: "2rem",
      }}
    >
      <div style={{ maxWidth: 520, width: "100%" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 24 }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              background: "#F97316",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
            }}
          >
            C
          </div>
          <span style={{ fontWeight: 600 }}>Callaloo RFP</span>
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12 }}>
          Configure Clerk to continue
        </h1>
        <p style={{ color: "#9CA3AF", fontSize: 14, lineHeight: 1.6 }}>
          The frontend booted, but no <code style={codeStyle}>VITE_CLERK_PUBLISHABLE_KEY</code> is
          set. Create <code style={codeStyle}>artifacts/web/.env.local</code> from{" "}
          <code style={codeStyle}>.env.example</code> and add a publishable key from{" "}
          <a
            href="https://dashboard.clerk.com"
            target="_blank"
            rel="noreferrer"
            style={{ color: "#F97316" }}
          >
            dashboard.clerk.com
          </a>
          , then refresh.
        </p>
        <p style={{ color: "#9CA3AF", fontSize: 14, lineHeight: 1.6, marginTop: 16 }}>
          Until then, the api-server can still be tested with{" "}
          <code style={codeStyle}>curl http://localhost:8081/api/healthz</code>.
        </p>
      </div>
    </div>
  );
}

const codeStyle: React.CSSProperties = {
  background: "rgba(255,255,255,0.06)",
  padding: "2px 6px",
  borderRadius: 4,
  fontFamily: "ui-monospace, monospace",
  fontSize: 12,
};
